import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  ChevronLeft, ChevronRight, Check, Plus, Trash2, Copy,
  MapPin, Calendar, Users, DollarSign, Clock, Sparkles,
  Image, GripVertical, Hotel, Truck, Shield, X,
  FileText, Eye, Star, Loader2, ArrowLeft, CheckCircle2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STEPS = [
  { id: 1, label: 'Trip Basics', icon: FileText },
  { id: 2, label: 'Overview', icon: Sparkles },
  { id: 3, label: 'Day Plan', icon: Calendar },
  { id: 4, label: 'Logistics', icon: Truck },
  { id: 5, label: 'Pricing', icon: DollarSign },
  { id: 6, label: 'Review', icon: Eye },
];

const INCLUSION_PRESETS = [
  'Accommodation', 'All meals', 'Park fees', 'Transport', 'Guide',
  'Airport transfers', 'Drinking water', 'Safari vehicle', 'Binoculars'
];
const EXCLUSION_PRESETS = [
  'International flights', 'Visa fees', 'Travel insurance', 'Personal expenses',
  'Tips & gratuities', 'Alcoholic beverages', 'Laundry', 'Optional activities'
];

const DAY_TEMPLATES = [
  { name: 'Safari Day', activities: 'Morning game drive, Afternoon game drive', accommodation: 'Safari Lodge', meals: 'Breakfast, Lunch, Dinner', transport: '4x4 Safari Vehicle' },
  { name: 'Beach Day', activities: 'Beach relaxation, Snorkeling, Sunset cruise', accommodation: 'Beach Resort', meals: 'Breakfast, Lunch, Dinner', transport: 'Hotel shuttle' },
  { name: 'Mountain Day', activities: 'Mountain trek, Nature walk', accommodation: 'Mountain Lodge', meals: 'Breakfast, Packed lunch, Dinner', transport: 'Hiking' },
  { name: 'Cultural Day', activities: 'Village visit, Local market, Cultural show', accommodation: 'Boutique Hotel', meals: 'Breakfast, Local lunch, Dinner', transport: 'Private vehicle' },
  { name: 'Transfer Day', activities: 'Scenic drive, Rest stop, Check-in', accommodation: 'Hotel', meals: 'Breakfast, Lunch', transport: 'Private vehicle' },
];

export default function ItineraryWizard() {
  const { tripId } = useParams();
  const { api } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [trip, setTrip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [isCurated, setIsCurated] = useState(false);

  const [form, setForm] = useState({
    title: '',
    duration_days: 1,
    description: '',
    highlights: [''],
    customization_note: '',
    daily_plan: [{ day: 1, title: '', activities: '', accommodation: '', meals: 'Breakfast, Lunch, Dinner', transport: '' }],
    accommodation_type: '',
    lodge_names: [''],
    transport_type: '',
    guide_included: true,
    transfers_included: true,
    total_price: '',
    price_per_person: '',
    currency: 'USD',
    inclusions: [],
    exclusions: [],
    upgrade_options: [''],
  });

  const fetchTrip = useCallback(async () => {
    try {
      const res = await api.get(`/trips/${tripId}`);
      const tripData = res.data;
      setTrip(tripData);

      const curated = tripData.lead_type === 'curated';
      setIsCurated(curated);

      // If curated, fetch the source itinerary and auto-fill
      if (curated && tripData.source_itinerary_id) {
        try {
          const srcRes = await api.get(`/itineraries/${tripData.source_itinerary_id}`);
          const src = srcRes.data;
          setForm({
            title: src.title || '',
            duration_days: src.duration_days || 1,
            description: src.description || '',
            highlights: src.highlights?.length ? src.highlights : [''],
            customization_note: src.customization_note || '',
            daily_plan: src.daily_plan?.length
              ? src.daily_plan.map((d, i) => ({
                  day: i + 1,
                  title: d.title || `Day ${i + 1}`,
                  activities: Array.isArray(d.activities) ? d.activities.join(', ') : (d.activities || ''),
                  accommodation: d.accommodation || '',
                  meals: Array.isArray(d.meals) ? d.meals.join(', ') : (d.meals || 'Breakfast, Lunch, Dinner'),
                  transport: d.transport || '',
                }))
              : [{ day: 1, title: 'Day 1', activities: '', accommodation: '', meals: 'Breakfast, Lunch, Dinner', transport: '' }],
            accommodation_type: src.accommodation_type || '',
            lodge_names: src.lodge_names?.length ? src.lodge_names : [''],
            transport_type: src.transport_type || '',
            guide_included: src.guide_included ?? true,
            transfers_included: src.transfers_included ?? true,
            total_price: src.total_price ? String(src.total_price) : '',
            price_per_person: src.price_per_person ? String(src.price_per_person) : '',
            currency: src.currency || 'USD',
            inclusions: src.inclusions || [],
            exclusions: src.exclusions || [],
            upgrade_options: src.upgrade_options?.length ? src.upgrade_options : [''],
          });
          return;
        } catch {
          // Fallback to default duration-based init if source fetch fails
        }
      }

      // Default init for custom leads — pre-fill from trip data where possible
      const dur = tripData.travel_dates?.start && tripData.travel_dates?.end
        ? Math.ceil((new Date(tripData.travel_dates.end) - new Date(tripData.travel_dates.start)) / 86400000)
        : 1;
      const d = Math.max(dur, 1);
      const destinations = [...(tripData.specific_destinations || []), ...(tripData.destination_interests || [])];
      const titlePrefix = destinations.length > 0 ? destinations.slice(0, 2).join(' & ') : 'Tanzania';
      const groupLabel = tripData.group_size ? `${tripData.group_size} traveler${tripData.group_size > 1 ? 's' : ''}` : '';
      const budgetHint = tripData.budget_range || '';
      setForm(prev => ({
        ...prev,
        title: `${titlePrefix} ${d}-Day Safari${groupLabel ? ` for ${groupLabel}` : ''}`,
        duration_days: d,
        description: tripData.broadcast_summary || `A ${d}-day${destinations.length ? ` ${destinations.join(', ')}` : ''} experience${budgetHint ? ` (${budgetHint} budget)` : ''}.`,
        daily_plan: Array.from({ length: d }, (_, i) => ({
          day: i + 1,
          title: destinations[i] ? `Day ${i + 1} — ${destinations[i]}` : `Day ${i + 1}`,
          activities: '', accommodation: '',
          meals: 'Breakfast, Lunch, Dinner', transport: ''
        })),
      }));
    } catch {
      toast.error('Failed to load trip details');
      navigate('/operator');
    } finally { setLoading(false); }
  }, [api, tripId, navigate]);

  useEffect(() => { fetchTrip(); }, [fetchTrip]);

  const updateForm = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const updateDayPlan = (idx, field, val) => {
    setForm(prev => {
      const plans = [...prev.daily_plan];
      plans[idx] = { ...plans[idx], [field]: val };
      return { ...prev, daily_plan: plans };
    });
  };

  const addDay = () => {
    setForm(prev => ({
      ...prev,
      duration_days: prev.duration_days + 1,
      daily_plan: [...prev.daily_plan, {
        day: prev.daily_plan.length + 1, title: `Day ${prev.daily_plan.length + 1}`,
        activities: '', accommodation: '', meals: 'Breakfast, Lunch, Dinner', transport: ''
      }]
    }));
  };

  const removeDay = (idx) => {
    if (form.daily_plan.length <= 1) return;
    setForm(prev => {
      const plans = prev.daily_plan.filter((_, i) => i !== idx).map((d, i) => ({ ...d, day: i + 1 }));
      return { ...prev, duration_days: plans.length, daily_plan: plans };
    });
  };

  const duplicateDay = (idx) => {
    setForm(prev => {
      const src = prev.daily_plan[idx];
      const plans = [...prev.daily_plan];
      plans.splice(idx + 1, 0, { ...src, day: idx + 2, title: `${src.title} (copy)` });
      const renumbered = plans.map((d, i) => ({ ...d, day: i + 1 }));
      return { ...prev, duration_days: renumbered.length, daily_plan: renumbered };
    });
  };

  const applyTemplate = (idx, template) => {
    updateDayPlan(idx, 'activities', template.activities);
    updateDayPlan(idx, 'accommodation', template.accommodation);
    updateDayPlan(idx, 'meals', template.meals);
    updateDayPlan(idx, 'transport', template.transport);
  };

  const toggleInclusion = (item) => {
    setForm(prev => ({
      ...prev,
      inclusions: prev.inclusions.includes(item)
        ? prev.inclusions.filter(i => i !== item)
        : [...prev.inclusions, item]
    }));
  };

  const toggleExclusion = (item) => {
    setForm(prev => ({
      ...prev,
      exclusions: prev.exclusions.includes(item)
        ? prev.exclusions.filter(i => i !== item)
        : [...prev.exclusions, item]
    }));
  };

  const updateListItem = (field, idx, val) => {
    setForm(prev => {
      const arr = [...prev[field]];
      arr[idx] = val;
      return { ...prev, [field]: arr };
    });
  };

  const addListItem = (field) => {
    setForm(prev => ({ ...prev, [field]: [...prev[field], ''] }));
  };

  const removeListItem = (field, idx) => {
    setForm(prev => ({ ...prev, [field]: prev[field].filter((_, i) => i !== idx) }));
  };

  const canAdvance = () => {
    if (step === 1) return form.title.trim().length > 0;
    if (step === 2) return form.description.trim().length > 0;
    if (step === 3) return form.daily_plan.some(d => d.activities.trim().length > 0);
    if (step === 5) return form.total_price > 0;
    return true;
  };

  const generateContent = async () => {
    setGenerating(true);
    try {
      const res = await api.post('/itinerary/generate', {
        trip_id: tripId,
        step,
        current_form: form,
      });
      const g = res.data.generated;

      if (step === 1 && g.title) {
        updateForm('title', g.title);
      }
      if (step === 2) {
        if (g.description) updateForm('description', g.description);
        if (g.highlights?.length) updateForm('highlights', g.highlights);
        if (g.customization_note) updateForm('customization_note', g.customization_note);
      }
      if (step === 3 && g.daily_plan?.length) {
        setForm(prev => ({
          ...prev,
          duration_days: g.daily_plan.length,
          daily_plan: g.daily_plan.map((d, i) => ({
            day: i + 1,
            title: d.title || `Day ${i + 1}`,
            activities: Array.isArray(d.activities) ? d.activities.join(', ') : (d.activities || ''),
            accommodation: d.accommodation || '',
            meals: Array.isArray(d.meals) ? d.meals.join(', ') : (d.meals || 'Breakfast, Lunch, Dinner'),
            transport: d.transport || '',
          }))
        }));
      }
      if (step === 4) {
        if (g.accommodation_type) updateForm('accommodation_type', g.accommodation_type);
        if (g.lodge_names?.length) updateForm('lodge_names', g.lodge_names);
        if (g.transport_type) updateForm('transport_type', g.transport_type);
        if (g.guide_included !== undefined) updateForm('guide_included', g.guide_included);
        if (g.transfers_included !== undefined) updateForm('transfers_included', g.transfers_included);
      }
      if (step === 5) {
        if (g.total_price) updateForm('total_price', String(g.total_price));
        if (g.price_per_person) updateForm('price_per_person', String(g.price_per_person));
        if (g.inclusions?.length) updateForm('inclusions', g.inclusions);
        if (g.exclusions?.length) updateForm('exclusions', g.exclusions);
        if (g.upgrade_options?.length) updateForm('upgrade_options', g.upgrade_options);
      }
      toast.success('Content generated! Review and edit as needed.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to generate content');
    } finally { setGenerating(false); }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        trip_id: tripId,
        title: form.title,
        description: form.description,
        total_price: parseFloat(form.total_price) || 0,
        currency: form.currency,
        duration_days: form.duration_days,
        daily_plan: form.daily_plan.map(d => ({
          day: d.day,
          title: d.title,
          activities: d.activities.split(',').map(s => s.trim()).filter(Boolean),
          accommodation: d.accommodation,
          meals: d.meals.split(',').map(s => s.trim()).filter(Boolean),
          transport: d.transport,
        })),
        inclusions: form.inclusions.filter(Boolean),
        exclusions: form.exclusions.filter(Boolean),
        highlights: form.highlights.filter(h => h.trim()),
        customization_note: form.customization_note || null,
        accommodation_type: form.accommodation_type || null,
        lodge_names: form.lodge_names.filter(n => n.trim()) || null,
        transport_type: form.transport_type || null,
        guide_included: form.guide_included,
        transfers_included: form.transfers_included,
        price_per_person: parseFloat(form.price_per_person) || null,
        upgrade_options: form.upgrade_options.filter(o => o.trim()) || null,
      };
      await api.post('/itineraries', payload);
      toast.success('Proposal submitted successfully!');
      navigate('/operator');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to submit proposal');
    } finally { setSubmitting(false); }
  };

  const formatDate = (d) => {
    if (!d) return '—';
    try { return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
    catch { return '—'; }
  };

  if (loading) return (
    <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
      <Loader2 className="w-6 h-6 animate-spin text-zinc-400" />
    </div>
  );

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="itinerary-wizard">
      {/* Top Bar */}
      <header className="sticky top-0 z-40 bg-white border-b border-zinc-200">
        <div className="max-w-5xl mx-auto px-6 h-14 flex items-center justify-between">
          <button onClick={() => navigate('/operator')} className="flex items-center gap-2 text-sm text-zinc-500 hover:text-zinc-900 transition" data-testid="wizard-back">
            <ArrowLeft size={16} /> Back to Dashboard
          </button>
          <div className="text-xs text-zinc-400 font-medium">Step {step} of {STEPS.length}</div>
          <div className="w-32" />
        </div>
      </header>

      {/* Progress Bar */}
      <div className="bg-white border-b border-zinc-100">
        <div className="max-w-5xl mx-auto px-6 py-3">
          <div className="flex items-center gap-1">
            {STEPS.map((s, i) => {
              const StepIcon = s.icon;
              const isActive = step === s.id;
              const isDone = step > s.id;
              return (
                <div key={s.id} className="flex items-center flex-1">
                  <button
                    onClick={() => { if (isDone || isActive) setStep(s.id); }}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-all
                      ${isActive ? 'bg-zinc-900 text-white' : isDone ? 'bg-zinc-100 text-zinc-700 hover:bg-zinc-200' : 'text-zinc-400 cursor-default'}`}
                    data-testid={`wizard-step-${s.id}`}
                    disabled={!isDone && !isActive}
                  >
                    {isDone ? <CheckCircle2 size={13} /> : <StepIcon size={13} />}
                    <span className="hidden sm:inline">{s.label}</span>
                  </button>
                  {i < STEPS.length - 1 && <div className={`flex-1 h-px mx-1 ${isDone ? 'bg-zinc-300' : 'bg-zinc-200'}`} />}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div className="max-w-5xl mx-auto px-6 py-8">

        {/* ===== STEP 1: TRIP BASICS ===== */}
        {step === 1 && (
          <div data-testid="wizard-step1">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h1 className="text-xl font-bold text-zinc-900 mb-1">Trip Basics</h1>
                <p className="text-sm text-zinc-500">Review the traveler's request and set your proposal title</p>
              </div>
              {!isCurated && <Button onClick={generateContent} disabled={generating} variant="outline" className="rounded-xl border-zinc-200 text-sm h-9 font-medium gap-2" data-testid="ai-generate-btn">
                {generating ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                {generating ? 'Generating...' : 'Auto Generate'}
              </Button>}
            </div>

            {/* Traveler Request Summary Card */}
            {trip && (
              <div className="bg-zinc-900 rounded-2xl p-6 mb-8" data-testid="traveler-snapshot">
                <p className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold mb-3">Traveler Request Summary</p>
                <p className="text-sm text-zinc-200 leading-relaxed mb-5">
                  {trip.broadcast_summary || `${trip.destination_interests?.join(', ')} for ${trip.group_size} travelers`}
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {[
                    { icon: MapPin, label: 'Destinations', value: [...(trip.specific_destinations || []), ...(trip.destination_interests || [])].slice(0, 3).join(', ') || '—' },
                    { icon: Calendar, label: 'Dates', value: trip.travel_dates?.start ? `${formatDate(trip.travel_dates.start)} – ${formatDate(trip.travel_dates.end)}` : 'Flexible' },
                    { icon: Users, label: 'Travelers', value: `${trip.group_size} person${trip.group_size !== 1 ? 's' : ''}` },
                    { icon: DollarSign, label: 'Budget', value: trip.budget_range ? `$${trip.budget_range.min?.toLocaleString()} – $${trip.budget_range.max?.toLocaleString()}` : '—' },
                  ].map((d, i) => (
                    <div key={i} className="bg-white/10 rounded-xl p-3">
                      <div className="flex items-center gap-1.5 mb-1">
                        <d.icon size={12} className="text-zinc-400" />
                        <span className="text-[10px] uppercase tracking-wider text-zinc-400 font-semibold">{d.label}</span>
                      </div>
                      <p className="text-sm font-semibold text-white truncate">{d.value}</p>
                    </div>
                  ))}
                </div>
                {trip.preferences?.length > 0 && (
                  <div className="mt-4 pt-3 border-t border-white/10">
                    <p className="text-[10px] uppercase tracking-wider text-zinc-400 font-semibold mb-2">Preferences</p>
                    <div className="flex flex-wrap gap-1.5">
                      {trip.preferences.map(p => (
                        <span key={p} className="px-2.5 py-1 bg-white/10 text-zinc-300 text-xs font-medium rounded-lg">{p}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Operator Input */}
            <div className="space-y-5">
              <div>
                <Label className="text-sm font-medium text-zinc-700 mb-1.5 block">Proposal Title *</Label>
                <Input
                  value={form.title}
                  onChange={e => updateForm('title', e.target.value)}
                  placeholder="e.g., 7-Day Serengeti & Zanzibar Adventure"
                  className="rounded-xl border-zinc-200 h-11 text-sm"
                  data-testid="wizard-title"
                />
              </div>
              <div>
                <Label className="text-sm font-medium text-zinc-700 mb-1.5 block">Final Duration (days)</Label>
                <Input
                  type="number" min={1} max={30}
                  value={form.duration_days}
                  onChange={e => {
                    const n = parseInt(e.target.value) || 1;
                    setForm(prev => ({
                      ...prev,
                      duration_days: n,
                      daily_plan: Array.from({ length: n }, (_, i) =>
                        prev.daily_plan[i] || { day: i + 1, title: `Day ${i + 1}`, activities: '', accommodation: '', meals: 'Breakfast, Lunch, Dinner', transport: '' }
                      ).map((d, i) => ({ ...d, day: i + 1 }))
                    }));
                  }}
                  className="rounded-xl border-zinc-200 h-11 text-sm w-32"
                  data-testid="wizard-duration"
                />
              </div>
            </div>
          </div>
        )}

        {/* ===== STEP 2: OVERVIEW & POSITIONING ===== */}
        {step === 2 && (
          <div data-testid="wizard-step2">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h1 className="text-xl font-bold text-zinc-900 mb-1">Overview & Positioning</h1>
                <p className="text-sm text-zinc-500">Sell the experience — describe what makes this trip special</p>
              </div>
              {!isCurated && <Button onClick={generateContent} disabled={generating} variant="outline" className="rounded-xl border-zinc-200 text-sm h-9 font-medium gap-2" data-testid="ai-generate-btn">
                {generating ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                {generating ? 'Generating...' : 'Auto Generate'}
              </Button>}
            </div>

            <div className="space-y-6">
              <div>
                <Label className="text-sm font-medium text-zinc-700 mb-1.5 block">Short Description *</Label>
                <textarea
                  value={form.description}
                  onChange={e => updateForm('description', e.target.value)}
                  className="w-full rounded-xl border border-zinc-200 px-4 py-3 text-sm min-h-[100px] resize-none focus:border-zinc-400 focus:ring-1 focus:ring-zinc-200 transition"
                  placeholder="Describe the experience in 2-4 lines. What will the traveler feel? What makes this unique?"
                  data-testid="wizard-description"
                />
              </div>

              {/* Highlights */}
              <div>
                <Label className="text-sm font-medium text-zinc-700 mb-1.5 block">Experience Highlights</Label>
                <p className="text-xs text-zinc-400 mb-3">Key moments and selling points of this trip</p>
                <div className="space-y-2">
                  {form.highlights.map((h, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <Star size={14} className="text-zinc-300 shrink-0" />
                      <Input
                        value={h}
                        onChange={e => updateListItem('highlights', i, e.target.value)}
                        placeholder={i === 0 ? 'e.g., Safari in the Serengeti' : i === 1 ? 'e.g., Beach relaxation in Zanzibar' : 'Add another highlight...'}
                        className="rounded-xl border-zinc-200 h-10 text-sm flex-1"
                        data-testid={`wizard-highlight-${i}`}
                      />
                      {form.highlights.length > 1 && (
                        <button onClick={() => removeListItem('highlights', i)} className="text-zinc-300 hover:text-red-400 transition">
                          <X size={14} />
                        </button>
                      )}
                    </div>
                  ))}
                  <button onClick={() => addListItem('highlights')} className="text-xs text-zinc-500 hover:text-zinc-900 font-medium flex items-center gap-1 mt-1 transition" data-testid="add-highlight">
                    <Plus size={12} /> Add highlight
                  </button>
                </div>
              </div>

              {/* Customization Note */}
              <div>
                <Label className="text-sm font-medium text-zinc-700 mb-1.5 block">Customization Note</Label>
                <Input
                  value={form.customization_note}
                  onChange={e => updateForm('customization_note', e.target.value)}
                  placeholder="e.g., Tailored for honeymoon couple / Designed for family with young kids"
                  className="rounded-xl border-zinc-200 h-10 text-sm"
                  data-testid="wizard-customization"
                />
              </div>
            </div>
          </div>
        )}

        {/* ===== STEP 3: DAY-BY-DAY PLAN ===== */}
        {step === 3 && (
          <div data-testid="wizard-step3">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h1 className="text-xl font-bold text-zinc-900 mb-1">Day-by-Day Plan</h1>
                <p className="text-sm text-zinc-500">{form.daily_plan.length} day{form.daily_plan.length !== 1 ? 's' : ''} planned</p>
              </div>
              <div className="flex items-center gap-2">
                {!isCurated && <Button onClick={generateContent} disabled={generating} variant="outline" className="rounded-xl border-zinc-200 text-sm h-9 font-medium gap-2" data-testid="ai-generate-btn">
                  {generating ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                  {generating ? 'Generating...' : 'Auto Generate'}
                </Button>}
                <Button onClick={addDay} variant="outline" className="rounded-xl border-zinc-200 text-sm h-9 font-medium" data-testid="add-day-btn">
                  <Plus size={14} className="mr-1.5" /> Add Day
                </Button>
              </div>
            </div>

            <div className="space-y-4">
              {form.daily_plan.map((day, idx) => (
                <div key={idx} className="bg-white rounded-2xl border border-zinc-200 overflow-hidden" data-testid={`day-card-${idx}`}>
                  {/* Day Header */}
                  <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-100 bg-zinc-50/50">
                    <div className="flex items-center gap-3">
                      <span className="w-8 h-8 rounded-lg bg-zinc-900 text-white text-xs font-bold flex items-center justify-center">{day.day}</span>
                      <Input
                        value={day.title}
                        onChange={e => updateDayPlan(idx, 'title', e.target.value)}
                        className="border-0 bg-transparent font-semibold text-sm text-zinc-900 p-0 h-auto focus-visible:ring-0 w-48"
                        placeholder="Day title..."
                      />
                    </div>
                    <div className="flex items-center gap-1">
                      {/* Templates dropdown */}
                      <div className="relative group">
                        <button className="p-1.5 rounded-lg text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 transition" title="Apply template">
                          <Sparkles size={14} />
                        </button>
                        <div className="absolute right-0 top-full mt-1 bg-white border border-zinc-200 rounded-xl shadow-lg py-1 w-44 hidden group-hover:block z-10">
                          {DAY_TEMPLATES.map(t => (
                            <button key={t.name} onClick={() => applyTemplate(idx, t)}
                              className="w-full text-left px-3 py-2 text-xs text-zinc-700 hover:bg-zinc-50 transition">
                              {t.name}
                            </button>
                          ))}
                        </div>
                      </div>
                      <button onClick={() => duplicateDay(idx)} className="p-1.5 rounded-lg text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 transition" title="Duplicate day">
                        <Copy size={14} />
                      </button>
                      {form.daily_plan.length > 1 && (
                        <button onClick={() => removeDay(idx)} className="p-1.5 rounded-lg text-zinc-400 hover:text-red-500 hover:bg-red-50 transition" title="Remove day">
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Day Content */}
                  <div className="p-5 grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-medium text-zinc-500 mb-1.5 block">Activities</label>
                      <textarea
                        value={day.activities}
                        onChange={e => updateDayPlan(idx, 'activities', e.target.value)}
                        placeholder="Morning game drive, Bush breakfast, Afternoon drive..."
                        className="w-full rounded-xl border border-zinc-200 px-3 py-2.5 text-sm min-h-[70px] resize-none focus:border-zinc-400 focus:ring-1 focus:ring-zinc-200 transition"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-zinc-500 mb-1.5 block">Accommodation</label>
                      <Input
                        value={day.accommodation}
                        onChange={e => updateDayPlan(idx, 'accommodation', e.target.value)}
                        placeholder="e.g., Serengeti Safari Lodge"
                        className="rounded-xl border-zinc-200 h-10 text-sm"
                      />
                      <label className="text-xs font-medium text-zinc-500 mb-1.5 mt-3 block">Transport</label>
                      <Input
                        value={day.transport}
                        onChange={e => updateDayPlan(idx, 'transport', e.target.value)}
                        placeholder="e.g., 4x4 Safari Vehicle"
                        className="rounded-xl border-zinc-200 h-10 text-sm"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="text-xs font-medium text-zinc-500 mb-1.5 block">Meals</label>
                      <Input
                        value={day.meals}
                        onChange={e => updateDayPlan(idx, 'meals', e.target.value)}
                        placeholder="Breakfast, Lunch, Dinner"
                        className="rounded-xl border-zinc-200 h-10 text-sm"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ===== STEP 4: ACCOMMODATION & LOGISTICS ===== */}
        {step === 4 && (
          <div data-testid="wizard-step4">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h1 className="text-xl font-bold text-zinc-900 mb-1">Accommodation & Logistics</h1>
                <p className="text-sm text-zinc-500">Define the comfort level and logistics of the trip</p>
              </div>
              {!isCurated && <Button onClick={generateContent} disabled={generating} variant="outline" className="rounded-xl border-zinc-200 text-sm h-9 font-medium gap-2" data-testid="ai-generate-btn">
                {generating ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                {generating ? 'Generating...' : 'Auto Generate'}
              </Button>}
            </div>

            <div className="grid lg:grid-cols-2 gap-8">
              {/* Accommodation */}
              <div className="space-y-5">
                <div className="flex items-center gap-2 mb-1">
                  <Hotel size={16} className="text-zinc-500" />
                  <h2 className="text-sm font-semibold text-zinc-900">Accommodation</h2>
                </div>

                <div>
                  <Label className="text-xs font-medium text-zinc-500 mb-2 block">Accommodation Level</Label>
                  <div className="grid grid-cols-3 gap-2">
                    {['Budget', 'Mid-range', 'Luxury'].map(type => (
                      <button
                        key={type}
                        onClick={() => updateForm('accommodation_type', type.toLowerCase())}
                        className={`py-3 px-4 rounded-xl border text-sm font-medium transition-all
                          ${form.accommodation_type === type.toLowerCase()
                            ? 'bg-zinc-900 text-white border-zinc-900'
                            : 'bg-white text-zinc-600 border-zinc-200 hover:border-zinc-300'}`}
                        data-testid={`accom-${type.toLowerCase()}`}
                      >
                        {type}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <Label className="text-xs font-medium text-zinc-500 mb-2 block">Specific Lodges / Hotels (optional)</Label>
                  <div className="space-y-2">
                    {form.lodge_names.map((name, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <Input
                          value={name}
                          onChange={e => updateListItem('lodge_names', i, e.target.value)}
                          placeholder="e.g., Serengeti Serena Lodge"
                          className="rounded-xl border-zinc-200 h-10 text-sm flex-1"
                        />
                        {form.lodge_names.length > 1 && (
                          <button onClick={() => removeListItem('lodge_names', i)} className="text-zinc-300 hover:text-red-400 transition">
                            <X size={14} />
                          </button>
                        )}
                      </div>
                    ))}
                    <button onClick={() => addListItem('lodge_names')} className="text-xs text-zinc-500 hover:text-zinc-900 font-medium flex items-center gap-1 mt-1 transition" data-testid="add-lodge">
                      <Plus size={12} /> Add lodge
                    </button>
                  </div>
                </div>
              </div>

              {/* Logistics */}
              <div className="space-y-5">
                <div className="flex items-center gap-2 mb-1">
                  <Truck size={16} className="text-zinc-500" />
                  <h2 className="text-sm font-semibold text-zinc-900">Logistics</h2>
                </div>

                <div>
                  <Label className="text-xs font-medium text-zinc-500 mb-1.5 block">Primary Transport</Label>
                  <Input
                    value={form.transport_type}
                    onChange={e => updateForm('transport_type', e.target.value)}
                    placeholder="e.g., 4x4 Safari vehicle, Domestic flights"
                    className="rounded-xl border-zinc-200 h-10 text-sm"
                    data-testid="wizard-transport"
                  />
                </div>

                <div className="space-y-3">
                  {[
                    { field: 'guide_included', label: 'Professional Guide Included', icon: Shield },
                    { field: 'transfers_included', label: 'Airport Transfers Included', icon: Truck },
                  ].map(opt => (
                    <button
                      key={opt.field}
                      onClick={() => updateForm(opt.field, !form[opt.field])}
                      className={`w-full flex items-center gap-3 p-4 rounded-xl border transition-all text-left
                        ${form[opt.field]
                          ? 'bg-zinc-900 text-white border-zinc-900'
                          : 'bg-white text-zinc-600 border-zinc-200 hover:border-zinc-300'}`}
                      data-testid={`toggle-${opt.field}`}
                    >
                      <opt.icon size={16} className={form[opt.field] ? 'text-zinc-300' : 'text-zinc-400'} />
                      <span className="text-sm font-medium flex-1">{opt.label}</span>
                      <div className={`w-5 h-5 rounded-md flex items-center justify-center ${form[opt.field] ? 'bg-white/20' : 'border border-zinc-300'}`}>
                        {form[opt.field] && <Check size={12} />}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ===== STEP 5: PRICING & INCLUSIONS ===== */}
        {step === 5 && (
          <div data-testid="wizard-step5">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h1 className="text-xl font-bold text-zinc-900 mb-1">Pricing & Inclusions</h1>
                <p className="text-sm text-zinc-500">Set a clear, trustworthy pricing structure</p>
              </div>
              {!isCurated && <Button onClick={generateContent} disabled={generating} variant="outline" className="rounded-xl border-zinc-200 text-sm h-9 font-medium gap-2" data-testid="ai-generate-btn">
                {generating ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                {generating ? 'Generating...' : 'Auto Generate'}
              </Button>}
            </div>

            <div className="grid lg:grid-cols-2 gap-8">
              {/* Pricing */}
              <div className="space-y-5">
                <h2 className="text-sm font-semibold text-zinc-900">Pricing</h2>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs font-medium text-zinc-500 mb-1.5 block">Total Cost (USD) *</Label>
                    <Input
                      type="number" min={0}
                      value={form.total_price}
                      onChange={e => {
                        const total = e.target.value;
                        updateForm('total_price', total);
                        if (total && trip?.group_size) {
                          updateForm('price_per_person', (parseFloat(total) / trip.group_size).toFixed(0));
                        }
                      }}
                      placeholder="e.g., 5000"
                      className="rounded-xl border-zinc-200 h-11 text-sm"
                      data-testid="wizard-total-price"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-medium text-zinc-500 mb-1.5 block">Per Person</Label>
                    <Input
                      type="number" min={0}
                      value={form.price_per_person}
                      onChange={e => updateForm('price_per_person', e.target.value)}
                      placeholder="Auto-calculated"
                      className="rounded-xl border-zinc-200 h-11 text-sm bg-zinc-50"
                      data-testid="wizard-per-person"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs font-medium text-zinc-500 mb-1.5 block">Currency</Label>
                  <Input
                    value={form.currency}
                    onChange={e => updateForm('currency', e.target.value)}
                    className="rounded-xl border-zinc-200 h-10 text-sm w-24"
                  />
                </div>

                {/* Upgrade Options */}
                <div className="mt-4">
                  <Label className="text-xs font-medium text-zinc-500 mb-2 block">Upgrade Options (optional)</Label>
                  <div className="space-y-2">
                    {form.upgrade_options.map((opt, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <Input
                          value={opt}
                          onChange={e => updateListItem('upgrade_options', i, e.target.value)}
                          placeholder="e.g., Luxury lodge upgrade +$500/person"
                          className="rounded-xl border-zinc-200 h-10 text-sm flex-1"
                        />
                        {form.upgrade_options.length > 1 && (
                          <button onClick={() => removeListItem('upgrade_options', i)} className="text-zinc-300 hover:text-red-400 transition">
                            <X size={14} />
                          </button>
                        )}
                      </div>
                    ))}
                    <button onClick={() => addListItem('upgrade_options')} className="text-xs text-zinc-500 hover:text-zinc-900 font-medium flex items-center gap-1 mt-1 transition" data-testid="add-upgrade">
                      <Plus size={12} /> Add option
                    </button>
                  </div>
                </div>
              </div>

              {/* Inclusions / Exclusions */}
              <div className="space-y-5">
                <div>
                  <h2 className="text-sm font-semibold text-zinc-900 mb-3">Included</h2>
                  <div className="flex flex-wrap gap-2">
                    {INCLUSION_PRESETS.map(item => (
                      <button
                        key={item}
                        onClick={() => toggleInclusion(item)}
                        className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium border transition-all
                          ${form.inclusions.includes(item)
                            ? 'bg-zinc-900 text-white border-zinc-900'
                            : 'bg-white text-zinc-600 border-zinc-200 hover:border-zinc-300'}`}
                        data-testid={`inclusion-${item.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        {form.inclusions.includes(item) ? <Check size={12} /> : <Plus size={12} />}
                        {item}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <h2 className="text-sm font-semibold text-zinc-900 mb-3">Excluded</h2>
                  <div className="flex flex-wrap gap-2">
                    {EXCLUSION_PRESETS.map(item => (
                      <button
                        key={item}
                        onClick={() => toggleExclusion(item)}
                        className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium border transition-all
                          ${form.exclusions.includes(item)
                            ? 'bg-red-50 text-red-700 border-red-200'
                            : 'bg-white text-zinc-600 border-zinc-200 hover:border-zinc-300'}`}
                        data-testid={`exclusion-${item.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        {form.exclusions.includes(item) ? <X size={12} /> : <Plus size={12} />}
                        {item}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ===== STEP 6: REVIEW & SUBMIT ===== */}
        {step === 6 && (
          <div data-testid="wizard-step6">
            <h1 className="text-xl font-bold text-zinc-900 mb-1">Review & Submit</h1>
            <p className="text-sm text-zinc-500 mb-8">Final check before sending your proposal</p>

            {/* Snapshot Alignment */}
            {trip && (
              <div className="bg-zinc-50 rounded-2xl border border-zinc-200 p-5 mb-6" data-testid="snapshot-alignment">
                <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-3">Snapshot Alignment</p>
                <div className="grid sm:grid-cols-3 gap-3">
                  {[
                    {
                      label: 'Budget',
                      ok: trip.budget_range && form.total_price && parseFloat(form.total_price) >= (trip.budget_range.min || 0) && parseFloat(form.total_price) <= (trip.budget_range.max || Infinity),
                      detail: trip.budget_range ? `Requested $${trip.budget_range.min?.toLocaleString()}–$${trip.budget_range.max?.toLocaleString()}, offering $${parseFloat(form.total_price || 0).toLocaleString()}` : 'No budget specified'
                    },
                    {
                      label: 'Duration',
                      ok: true,
                      detail: `${form.duration_days} days planned`
                    },
                    {
                      label: 'Group Size',
                      ok: true,
                      detail: `${trip.group_size} traveler${trip.group_size !== 1 ? 's' : ''}`
                    }
                  ].map((item, i) => (
                    <div key={i} className={`rounded-xl p-3 border ${item.ok ? 'bg-white border-zinc-200' : 'bg-amber-50 border-amber-200'}`}>
                      <div className="flex items-center gap-1.5 mb-1">
                        {item.ok ? <CheckCircle2 size={13} className="text-emerald-500" /> : <Clock size={13} className="text-amber-500" />}
                        <span className="text-xs font-semibold text-zinc-700">{item.label}</span>
                      </div>
                      <p className="text-[11px] text-zinc-500">{item.detail}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Full Preview */}
            <div className="bg-white rounded-2xl border border-zinc-200 overflow-hidden mb-6">
              {/* Preview Header */}
              <div className="bg-zinc-900 p-6">
                <h2 className="text-lg font-bold text-white">{form.title || 'Untitled Proposal'}</h2>
                <p className="text-sm text-zinc-300 mt-1">{form.duration_days} days &middot; ${parseFloat(form.total_price || 0).toLocaleString()} {form.currency} total</p>
                {form.price_per_person && <p className="text-xs text-zinc-400 mt-0.5">${parseFloat(form.price_per_person).toLocaleString()} per person</p>}
              </div>

              <div className="p-6 space-y-6">
                {/* Description */}
                {form.description && (
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold mb-1.5">Overview</p>
                    <p className="text-sm text-zinc-700 leading-relaxed">{form.description}</p>
                  </div>
                )}

                {/* Highlights */}
                {form.highlights.filter(h => h.trim()).length > 0 && (
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold mb-2">Highlights</p>
                    <div className="space-y-1.5">
                      {form.highlights.filter(h => h.trim()).map((h, i) => (
                        <div key={i} className="flex items-start gap-2">
                          <Star size={12} className="text-amber-400 mt-0.5 shrink-0" />
                          <span className="text-sm text-zinc-700">{h}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {form.customization_note && (
                  <div className="bg-zinc-50 rounded-xl px-4 py-3 border border-zinc-100">
                    <p className="text-xs text-zinc-500 italic">{form.customization_note}</p>
                  </div>
                )}

                {/* Day Plan Preview */}
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold mb-3">Day-by-Day Itinerary</p>
                  <div className="space-y-3">
                    {form.daily_plan.map((day, idx) => (
                      <div key={idx} className="flex gap-4" data-testid={`review-day-${idx}`}>
                        <div className="flex flex-col items-center">
                          <div className="w-7 h-7 rounded-lg bg-zinc-900 text-white text-xs font-bold flex items-center justify-center shrink-0">{day.day}</div>
                          {idx < form.daily_plan.length - 1 && <div className="w-px flex-1 bg-zinc-200 mt-1" />}
                        </div>
                        <div className="flex-1 pb-4">
                          <p className="text-sm font-semibold text-zinc-900">{day.title || `Day ${day.day}`}</p>
                          {day.activities && <p className="text-xs text-zinc-600 mt-0.5">{day.activities}</p>}
                          <div className="flex flex-wrap gap-3 mt-1.5 text-[11px] text-zinc-400">
                            {day.accommodation && <span>{day.accommodation}</span>}
                            {day.transport && <span>&middot; {day.transport}</span>}
                            {day.meals && <span>&middot; {day.meals}</span>}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Logistics */}
                {(form.accommodation_type || form.transport_type) && (
                  <div className="grid sm:grid-cols-2 gap-4">
                    {form.accommodation_type && (
                      <div>
                        <p className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold mb-1.5">Accommodation</p>
                        <p className="text-sm text-zinc-700 capitalize">{form.accommodation_type}</p>
                        {form.lodge_names.filter(n => n.trim()).length > 0 && (
                          <p className="text-xs text-zinc-500 mt-0.5">{form.lodge_names.filter(n => n.trim()).join(', ')}</p>
                        )}
                      </div>
                    )}
                    {form.transport_type && (
                      <div>
                        <p className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold mb-1.5">Transport</p>
                        <p className="text-sm text-zinc-700">{form.transport_type}</p>
                        <div className="flex gap-3 mt-1 text-xs text-zinc-500">
                          {form.guide_included && <span>Guide included</span>}
                          {form.transfers_included && <span>Transfers included</span>}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Inclusions/Exclusions */}
                <div className="grid sm:grid-cols-2 gap-4">
                  {form.inclusions.length > 0 && (
                    <div>
                      <p className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold mb-2">Included</p>
                      <div className="space-y-1">
                        {form.inclusions.map(item => (
                          <div key={item} className="flex items-center gap-2 text-sm text-zinc-700">
                            <Check size={12} className="text-emerald-500 shrink-0" /> {item}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {form.exclusions.length > 0 && (
                    <div>
                      <p className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold mb-2">Excluded</p>
                      <div className="space-y-1">
                        {form.exclusions.map(item => (
                          <div key={item} className="flex items-center gap-2 text-sm text-zinc-500">
                            <X size={12} className="text-red-400 shrink-0" /> {item}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Upgrade Options */}
                {form.upgrade_options.filter(o => o.trim()).length > 0 && (
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold mb-2">Upgrade Options</p>
                    <div className="space-y-1">
                      {form.upgrade_options.filter(o => o.trim()).map((o, i) => (
                        <div key={i} className="flex items-center gap-2 text-sm text-zinc-700">
                          <Sparkles size={12} className="text-amber-400 shrink-0" /> {o}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Edit Shortcuts */}
            <div className="flex flex-wrap gap-2 mb-6">
              {STEPS.slice(0, 5).map(s => (
                <button
                  key={s.id}
                  onClick={() => setStep(s.id)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs text-zinc-500 border border-zinc-200 hover:border-zinc-300 hover:text-zinc-700 bg-white transition"
                  data-testid={`edit-section-${s.id}`}
                >
                  <s.icon size={12} /> Edit {s.label}
                </button>
              ))}
            </div>

            {/* Submit */}
            <Button
              onClick={handleSubmit}
              disabled={submitting || !form.title || !form.total_price || !form.description}
              className="w-full bg-zinc-900 text-white hover:bg-zinc-800 rounded-xl py-3 h-12 text-sm font-semibold"
              data-testid="submit-proposal-btn"
            >
              {submitting ? <><Loader2 size={16} className="animate-spin mr-2" /> Submitting...</> : 'Submit Proposal'}
            </Button>
            <p className="text-center text-xs text-zinc-400 mt-2">Credits will be deducted upon submission</p>
          </div>
        )}

        {/* Navigation Footer */}
        {step < 6 && (
          <div className="flex items-center justify-between mt-10 pt-6 border-t border-zinc-200">
            <Button
              onClick={() => setStep(Math.max(1, step - 1))}
              disabled={step === 1}
              variant="outline"
              className="rounded-xl border-zinc-200 text-sm h-10 px-6 font-medium"
              data-testid="wizard-prev"
            >
              <ChevronLeft size={16} className="mr-1" /> Back
            </Button>
            <Button
              onClick={() => setStep(Math.min(6, step + 1))}
              disabled={!canAdvance()}
              className="rounded-xl bg-zinc-900 text-white hover:bg-zinc-800 text-sm h-10 px-6 font-medium"
              data-testid="wizard-next"
            >
              Next <ChevronRight size={16} className="ml-1" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
